import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faMoneyBillWave,
  faUser,
  faCalendarAlt,
  faFileAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";

const getSellerId = (seller) =>
  seller?.shopSellerId ??
  seller?.shop_seller_id ??
  seller?.shop_saller_id ??
  seller?.id ??
  null;

const getSellerFullName = (seller) =>
  seller?.user?.full_name || seller?.full_name || "";

const getSellerUsername = (seller) =>
  seller?.user?.username || seller?.username || "";

const getSellerPhone = (seller) => seller?.user?.phone || seller?.phone || "";

const getSellerDisplay = (seller) => {
  const fullName = getSellerFullName(seller);
  const username = getSellerUsername(seller);
  const phone = getSellerPhone(seller);

  const parts = [];
  if (fullName) parts.push(fullName);
  if (phone) parts.push(phone);
  if (username && username !== fullName) parts.push(username);

  return parts.length ? parts.join(" · ") : "Unknown seller";
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const EditSalesCloseout = ({
  isOpen,
  onClose,
  onUpdate,
  closeout,
  sellers = [],
}) => {
  const [formData, setFormData] = useState({
    sellerId: "",
    amount: "",
    date: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!closeout) return;

    const normalizedSellerId =
      closeout.shopSellerId ??
      closeout.shop_seller_id ??
      closeout.sellerId ??
      closeout.seller_id ??
      "";

    const normalizedDate =
      closeout.created_at ?? closeout.createdAt ?? closeout.date ?? "";

    setFormData({
      sellerId: normalizedSellerId ? String(normalizedSellerId) : "",
      amount:
        closeout.amount !== undefined && closeout.amount !== null
          ? String(closeout.amount)
          : "",
      date: toDateInputValue(normalizedDate),
      description: closeout.description || "",
    });
    setErrors({});
    setIsSubmitting(false);
  }, [closeout]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = () => {
    const newErrors = {};
    let errorMessage = "";

    if (!formData.sellerId) {
      newErrors.sellerId = "Please select a seller";
      errorMessage = "Please select a seller";
    }
    if (!formData.amount) {
      newErrors.amount = "Amount is required";
      errorMessage = "Please enter the total amount collected";
    } else if (
      isNaN(parseFloat(formData.amount)) ||
      parseFloat(formData.amount) <= 0
    ) {
      newErrors.amount = "Please enter a valid amount greater than 0";
      errorMessage = "Please enter a valid amount greater than 0";
    }
    if (!formData.date) {
      newErrors.date = "Date is required";
      errorMessage = "Please select a date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError(errorMessage || "Please fix all validation errors.");
      return;
    }

    setIsSubmitting(true);

    const seller = (sellers || []).find(
      (s) => String(getSellerId(s)) === String(formData.sellerId),
    );

    const payload = {
      closeoutId: closeout?.closeoutId ?? closeout?.closeout_id ?? closeout?.id,
      shopSellerId: formData.sellerId,
      sellerId: formData.sellerId,
      sellerName: getSellerFullName(seller) || getSellerUsername(seller),
      amount: parseFloat(formData.amount),
      created_at: formData.date,
      date: formData.date,
      description: formData.description.trim() || "",
    };

    try {
      const result = onUpdate(payload);

      if (result && typeof result.then === "function") {
        result
          .then(() => {
            toastSuccess("Sales closeout updated successfully!");
            setIsSubmitting(false);
            onClose();
          })
          .catch((error) => {
            toastError(
              `Failed to update closeout: ${error.message || "Please try again."}`,
            );
            setIsSubmitting(false);
          });
      } else {
        toastSuccess("Sales closeout updated successfully!");
        setIsSubmitting(false);
        onClose();
      }
    } catch (error) {
      toastError(
        `Failed to update closeout: ${error.message || "Please try again."}`,
      );
      setIsSubmitting(false);
    }
  };

  const selectedSeller = (sellers || []).find(
    (s) => String(getSellerId(s)) === String(formData.sellerId),
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Sales Closeout"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Update closeout
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Info Banner */}
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faEdit} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold">
              Edit sales closeout
            </p>
            <p className="text-xs text-gray-600 font-medium">
              Update sales closing information
            </p>
          </div>
        </div>

        {/* Seller */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faUser}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Seller<span className="text-red-700">*</span>
          </Label>
          <Select
            value={formData.sellerId}
            onValueChange={(value) => {
              setFormData((prev) => ({ ...prev, sellerId: value }));
              if (errors.sellerId) {
                setErrors((prev) => ({ ...prev, sellerId: "" }));
              }
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${
                errors.sellerId ? "border-red-500" : "border-gray-300"
              }`}
            >
              <SelectValue placeholder="Select seller">
                {selectedSeller ? getSellerDisplay(selectedSeller) : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(sellers || []).map((seller, idx) => {
                const id = getSellerId(seller);
                const fullName = getSellerFullName(seller);
                const username = getSellerUsername(seller);
                const phone = getSellerPhone(seller);

                return (
                  <SelectItem
                    key={id ?? `seller-${idx}`}
                    value={id != null ? String(id) : `seller-${idx}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {fullName || username || "Unknown seller"}
                      </span>
                      {phone && (
                        <span className="text-xs text-gray-600">{phone}</span>
                      )}
                      {username && username !== fullName && (
                        <span className="text-xs text-gray-500">
                          {username}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {errors.sellerId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.sellerId}
            </p>
          )}
        </div>

        {/* Amount */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Total Amount (TSH)<span className="text-red-700">*</span>
          </Label>
          <Input
            name="amount"
            type="number"
            min="0"
            step="100"
            value={formData.amount}
            onChange={handleChange}
            placeholder="Enter total amount collected"
            className={`border ${
              errors.amount ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.amount}
            </p>
          )}
        </div>

        {/* Date */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Date<span className="text-red-700">*</span>
          </Label>
          <Input
            name="date"
            type="date"
            value={formData.date}
            onChange={handleChange}
            className={`border ${
              errors.date ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.date && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.date}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 mb-2 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faFileAlt}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Description
          </Label>
          <Textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Optional notes about the sales (e.g., market day, bulk sales)"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 min-h-[100px] resize-y"
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditSalesCloseout;
